import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Download, Play, ShoppingCart, Check } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { videoService } from "@/services/videos";
import { cartsService } from "@/services/carts";
import { statisticsService } from "@/services/stats";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

interface VideoDetails {
  id: string;
  bought: number;
  court_id: number;
  court_name: string;
  brand_name: string;
  hour: string;
  storage_url: string;
  url: string;
  watch_count: number;
  price: number;
  purchase_count: boolean;
  download_count: number;
  created_at: string;
  updated_at: string;
  is_free: boolean;
}

const VideoDetail = () => {
  const [loading, setLoading] = useState(true);
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState<VideoDetails | null>(null);
  const [isPurchased, setIsPurchased] = useState(false);
  const [hasPlay, setHasPlay] = useState(0);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasPendingTransaction =
    JSON.parse(localStorage.getItem("pendingTransaction") || "[]").length > 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const data = await videoService.fetchVideoById(videoId || "101");

        setVideo({
          id: data.clip.id,
          bought: parseInt(data.clip.bought),
          court_id: parseInt(data.clip.court_id),
          court_name: data.clip.court_name,
          brand_name: data.clip.merchant_name,
          storage_url: data.clip.storage_url,
          hour: data.clip.created_at.toString(),
          url: data.clip.url,
          watch_count: data.clip.watch_count,
          price: data.clip.price,
          purchase_count: data.clip.purchase_count,
          download_count: data.clip.download_count,
          created_at: data.clip.created_at,
          updated_at: data.clip.updated_at,
          is_free: data.clip.price == "0",
        });

        setIsPurchased(data.clip.bought == "1");

      } catch (error) {
        console.error("Failed to fetch video details:", error);
      } finally {
        setTimeout(() => setLoading(false), 300);
      }
    };

    fetchData();
  }, [videoId]);

  const formatHour = (hour: number) => {
    return `${hour.toString().padStart(2, "0")}:00–${(hour + 1)
      .toString()
      .padStart(2, "0")}:00`;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  function getTimeRange(dateTimeStr: string): string {
    // Convert string to Date
    const date = new Date(dateTimeStr.replace(" ", "T"));

    // Copy and subtract 30 seconds
    const earlier = new Date(date.getTime() - 30 * 1000);

    // Format to HH:mm:ss
    const formatTime = (d: Date) => d.toTimeString().split(" ")[0]; // "12:09:40"

    return `${formatTime(earlier)} - ${formatTime(date)}`;
  }

  function getTimeRangeFromUrl(url: string): string {
    // Extract timestamp from URL
    const match = url.match(/(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}\.\d+)/);

    if (!match) return "";

    const rawTime = match[1];
    // example: 2026-03-06_14-52-29.677

    // Convert to ISO format
    const formatted = rawTime
      .replace("_", "T")     // 2026-03-06T14-52-29.677
      .replace(/-/g, ":")    // 2026:03:06T14:52:29.677
      .replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3");
    // fix date part back

    const date = new Date(formatted);

    // subtract 30 seconds
    const earlier = new Date(date.getTime() - 30 * 1000);

    // format HH:mm:ss
    const formatTime = (d: Date) => d.toTimeString().split(" ")[0];

    return `${formatTime(earlier)} - ${formatTime(date)}`;
  }

  function getDate(dateTimeStr: string) {
    // Convert string to Date
    const date = dateTimeStr.split(" ")[0];
    return date;
  }

  const handleVideoPlay = async () => {
    try {
      if (Number(hasPlay) == 0) {
        await statisticsService.addWatchCount(video.id);
        setHasPlay(1)
      }
    } catch (err) {
      console.error("Failed to increase watch count", err);
    }
  };

  const handlePurchase = async () => {
    setShowPurchaseDialog(false);
    setIsProcessing(true);

    // Mock purchase API call
    setTimeout(() => {
      const purchasedVideos = JSON.parse(
        localStorage.getItem("purchasedVideos") || "[]"
      );
      purchasedVideos.push(video?.id);
      localStorage.setItem("purchasedVideos", JSON.stringify(purchasedVideos));

      // Add to library
      const library = JSON.parse(localStorage.getItem("videoLibrary") || "[]");
      library.push({
        id: video?.id,
        court: video?.court_name,
        brand: video?.brand_name,
        date: video?.date,
        hour: video?.hour,
        url: video?.url,
      });
      localStorage.setItem("videoLibrary", JSON.stringify(library));

      setIsPurchased(true);
      setShowPurchaseDialog(false);
      setIsProcessing(false);

      toast({
        title: "Purchase successful!",
        description: "Video has been added to your library.",
      });
    }, 2000);
  };

  const handleAddToCart = async () => {
    setShowPurchaseDialog(false);
    setIsProcessing(true);
    try {
      const user_id = JSON.parse(localStorage.getItem("user"))?.id || "";
      if (!user_id) {
        toast({
          title: "Mohon login",
          description: "Login untuk menggunakan fitur library",
        });
        navigate("/login", {
          state: {
            from: "videos",
            path: `/videos/${videoId}`
          }
        });
        return;
      };
      if (!video) throw new Error("Video not loaded");

      const response = await cartsService.addToCart({
        user_id: user_id,
        clip_id: parseInt(video.id),
      });
      toast({
        title: "Added to cart",
        description: response?.message,
      });
    } catch (error) {
      console.error("Failed to add to cart:", error);
      toast({
        title: "Failed",
        description: error.response?.data?.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddToLibrary = async () => {
    setShowPurchaseDialog(false);
    setIsProcessing(true);
    try {
      const user_id = JSON.parse(localStorage.getItem("user"))?.id || "";
      if (!user_id) {
        toast({
          title: "Mohon login",
          description: "Login untuk menggunakan fitur library",
        });
        navigate("/login", {
          state: {
            from: "videos",
            path: `/videos/${videoId}`
          }
        });
        return;
      };
      if (!video) throw new Error("Video not loaded");

      const response = await cartsService.addToLibrary({
        user_id: user_id,
        clip_id: parseInt(video.id),
      });
      toast({
        title: "Added to your library",
        description: response?.message,
      });
    } catch (error) {
      console.error("Failed to add library:", error);
      toast({
        title: "Failed",
        description: error.response?.data?.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    statisticsService.addDownloadCount(video.id);
    if (!video?.url) {
      toast({
        title: "Open failed",
        description: "No video available to open.",
        variant: "destructive",
      });
      return;
    }

    // Open video URL in a new window / tab
    const secureUrl = video.url.replace(/^http:\/\//i, "https://");

    window.open(secureUrl, "_blank", "noopener,noreferrer");

    toast({
      title: "Download started",
      description: `Downloading ${video.court_name} video from ${getTimeRangeFromUrl(video.url.toString())}`,
    });
  };


  // const canWatch = video.is_free || isPurchased;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100  overflow-x-hidden">
      <main className="max-w-6xl mx-auto px-6 py-10">

        {/* HEADER */}
        <div className="mb-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-6 hover:bg-white/60 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <h1 className="text-4xl font-bold tracking-tight">
            Video Replay
          </h1>

          <p className="text-lg text-muted-foreground mt-2 flex items-center gap-2 min-w-0 flex-wrap">
            {loading ? (
              <>
                <Skeleton className="h-5 w-[120px]" />
                <span>at</span>
                <Skeleton className="h-5 w-full max-w-[150px]" />
                <Skeleton className="h-5 w-full max-w-[180px]" />
              </>
            ) : (
              <>
                {video?.court_name} at{" "}
                <span className="font-semibold">{video?.brand_name}</span> •{" "}
                {getTimeRangeFromUrl(video.url)}
              </>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* VIDEO PLAYER */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden rounded-2xl border bg-white/80 backdrop-blur-md shadow-lg">
              <div className="aspect-video relative overflow-hidden">
                {loading ? (
                  <div className="w-full h-full bg-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                  </div>
                ) : (
                  <video
                    controls
                    controlsList="nodownload"
                    className="w-full h-full object-cover"
                    autoPlay
                    onPlay={handleVideoPlay}
                  >
                    <source src={video.url} type="video/mp4" />
                  </video>
                )}
              </div>
            </Card>

            {/* ACTION BUTTON */}
            <div className="flex gap-4">
              {loading ? (
                <Skeleton className="h-12 w-full rounded-xl" />
              ) : video ? (
                video.is_free || isPurchased ? (
                  <Button
                    className="flex-1 rounded-xl text-lg"
                    size="lg"
                    onClick={handleDownload}
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Video
                  </Button>
                ) : (
                  <Button
                    className="flex-1 rounded-xl text-lg"
                    size="lg"
                    onClick={() => setShowPurchaseDialog(true)}
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Buy - {formatPrice(video.price)}
                  </Button>
                )
              ) : null}
            </div>
          </div>

          {/* VIDEO INFO SIDEBAR */}
          <div className="space-y-6">

            <Card className="rounded-2xl border bg-white/80 backdrop-blur-md shadow-md">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h3 className="text-xl font-semibold">Video Details</h3>

                  {loading ? (
                    <Skeleton className="h-6 w-20 rounded-full" />
                  ) : video ? (
                    <Badge
                      variant={video.is_free ? "default" : "secondary"}
                      className={
                        video.is_free
                          ? "bg-green-500 text-white"
                          : isPurchased
                          ? "bg-emerald-600 text-white"
                          : "bg-orange-500 text-white"
                      }
                    >
                      {video.is_free
                        ? "Free"
                        : isPurchased
                        ? "Purchased"
                        : formatPrice(video.price)}
                    </Badge>
                  ) : null}
                </div>

                <div className="space-y-4 text-sm">
                  {/* Court */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Court</span>
                    {loading ? (
                      <Skeleton className="h-4 w-24" />
                    ) : (
                      <span className="font-medium">{video?.court_name}</span>
                    )}
                  </div>

                  {/* Venue */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Venue</span>
                    {loading ? (
                      <Skeleton className="h-4 w-28" />
                    ) : (
                      <span className="font-medium">{video?.brand_name}</span>
                    )}
                  </div>

                  {/* Date */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    {loading ? (
                      <Skeleton className="h-4 w-20" />
                    ) : (
                      <span className="font-medium">{getDate(video?.hour)}</span>
                    )}
                  </div>

                  {/* Time */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time</span>
                    {loading ? (
                      <Skeleton className="h-4 w-32" />
                    ) : (
                      <span className="font-medium">
                        {getTimeRangeFromUrl(video?.url)}
                      </span>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    {loading ? (
                      <Skeleton className="h-4 w-16" />
                    ) : (
                      <span className="font-medium">30 Seconds</span>
                    )}
                  </div>

                  {/* Quality */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quality</span>
                    {loading ? (
                      <Skeleton className="h-4 w-20" />
                    ) : (
                      <span className="font-medium">HD 1080p</span>
                    )}
                  </div>

                  {/* Watch Count */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Watch Count</span>
                    {loading ? (
                      <Skeleton className="h-4 w-10" />
                    ) : (
                      <span className="font-medium">{video?.watch_count}</span>
                    )}
                  </div>

                  {/* Download Count */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Download Count</span>
                    {loading ? (
                      <Skeleton className="h-4 w-10" />
                    ) : (
                      <span className="font-medium">{video?.download_count}</span>
                    )}
                  </div>

                  {/* Purchase Count */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Purchase Count</span>
                    {loading ? (
                      <Skeleton className="h-4 w-10" />
                    ) : (
                      <span className="font-medium">{video?.purchase_count}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {isPurchased && (
              <Card className="rounded-2xl bg-emerald-50 border-emerald-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-emerald-600 mb-2">
                    <Check className="w-5 h-5" />
                    <span className="font-semibold">
                      Video Purchased
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This video is available in your library for unlimited viewing
                    and download.
                  </p>
                </CardContent>
              </Card>
            )}

            {video?.is_free && !isPurchased && (
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={handleAddToLibrary}
                disabled={isProcessing}
              >
                Add to My Library
              </Button>
            )}

            <Button
              variant="outline"
              className="w-full rounded-xl"
              onClick={() => navigate("/library")}
            >
              Go to My Library
            </Button>

          </div>
        </div>
      </main>

      {/* PURCHASE DIALOG tetap pakai punyamu, ga diubah */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="rounded-2xl max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>
              {hasPendingTransaction ? "Pending Payment" : "Confirm Purchase"}
            </DialogTitle>

            <DialogDescription>
              {hasPendingTransaction
                ? "You still have an unfinished payment. Please complete the payment before adding more videos to your cart."
                : `Are you sure you want to purchase this video replay for ${formatPrice(video?.price)}?`}
            </DialogDescription>
          </DialogHeader>

          {/* 🔴 If Pending Transaction Exists */}
          {hasPendingTransaction ? (
            <div className="pt-4 space-y-4">
              <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                Please complete your previous transaction first.
              </div>

              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPurchaseDialog(false)}
                  className="w-full sm:w-auto"
                >
                  Close
                </Button>

                <Button
                  onClick={() => {
                    setShowPurchaseDialog(false);
                    navigate("/cart");
                  }}
                  className="w-full sm:w-auto"
                >
                  Go to Cart
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              {/* 🟢 Normal Purchase UI */}
              <div className="py-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Video</span>
                  <span className="font-medium">
                    {video?.court_name}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Date</span>
                  <span className="font-medium">
                    {new Date(video?.hour).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex justify-between font-semibold border-t pt-3">
                  <span>Total</span>
                  <span>{formatPrice(video?.price)}</span>
                </div>
              </div>

              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPurchaseDialog(false)}
                  disabled={isProcessing}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleAddToCart}
                  disabled={isProcessing}
                  className="w-full sm:w-auto"
                >
                  {isProcessing ? "Processing..." : "Add to Cart"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoDetail;
